import { type RefSelectProps, Select } from 'antd'
import clsx from 'clsx'
import { type Ref, forwardRef, useMemo } from 'react'

import { type SingleOrMultipleSelectProps } from '@/components/types'
import { STATUSES } from '@/constants/common/message.constants'
import { type DcEntity } from '@/libs/api'
import { useSelectOnChangeHandler } from '@/libs/hooks'

import { mapToDcFieldName } from './map-to-dc-field-name'

type Props = SingleOrMultipleSelectProps<number, DcEntity> & {
  needDefaultDcId?: boolean
  options: DcEntity[]
}

/**
 * 拠点(物流センター)を選択するSelectのベースComponent
 */
export const DcSelect = forwardRef(
  (
    {
      className,
      needDefaultDcId = true,
      value,
      placeholder = STATUSES.UNSELECTED,
      mode,
      onChange,
      options: dcs,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const options = useMemo(() => {
      return dcs.map((dc) => ({
        ...dc,
        // nameで指定すると利用側で取得できるoption.nameの値が変わるため、labelで指定
        label: mapToDcFieldName(dc)
      }))
    }, [dcs])

    const handleChange = useSelectOnChangeHandler<number, DcEntity>({
      mode,
      onChange
    })

    const defaultValue = useMemo(() => {
      if (!value && !needDefaultDcId) {
        return null
      }

      if (Array.isArray(value)) {
        // 指定された value から指定可能拠点一覧に存在しない値を除外する
        return value.filter((x) => options.some((dc) => dc.id == x))
      }

      // 指定された value が指定可能拠点一覧に存在しない場合は先頭の拠点を使用する
      return value && options.some((x) => x.id === value)
        ? value
        : options[0]?.id
    }, [options, needDefaultDcId, value])

    return (
      <Select
        ref={ref}
        mode={mode}
        value={defaultValue}
        placeholder={placeholder}
        fieldNames={{ label: 'label', value: 'id' }}
        optionFilterProp="label"
        className={clsx(['min-w-[200px]', className])}
        options={options}
        onChange={handleChange}
        // 既定は候補幅 = セレクト幅（#963）。呼び出し側の指定を優先するため
        // spread より前に置く。後ろに置くと指定が破棄される（#4818）
        popupMatchSelectWidth={true}
        {...props}
      />
    )
  }
)
